import type { ClientType } from './Company';

/**
 * Roles de usuario de la plataforma.
 *
 * - admin:      administrador de la compañía. Ve y hace todo dentro de ella,
 *               gestiona sus usuarios y define los permisos de los regulares.
 * - regular:    accede solo a los módulos y acciones que le asigna un admin.
 * - viewer:     consulta la información permitida sin modificarla. Equivale al
 *               clientType 'view' y se sigue rigiendo por VIEWER_VIEW_CONFIG.
 * - superadmin: rol interno de Kila para monitoreo. No se asigna desde la UI.
 */
export type CompanyRole = 'admin' | 'regular' | 'viewer';
export type UserRole = CompanyRole | 'superadmin';

/**
 * manage_templates es la única acción que no aplica a todos los módulos: solo
 * el correo la declara, para que el admin pueda dejar a un regular escoger
 * plantillas al redactar sin poder crearlas ni editarlas.
 */
export type ModuleAction =
  | 'read'
  | 'create'
  | 'update'
  | 'delete'
  | 'export'
  | 'approve'
  | 'manage_templates';

export const MODULE_ACTIONS: readonly ModuleAction[] = [
  'read',
  'create',
  'update',
  'delete',
  'export',
  'approve',
  'manage_templates'
];

/** Roles que un admin puede asignar desde la gestión de usuarios. */
export const ASSIGNABLE_ROLES: readonly CompanyRole[] = ['admin', 'regular', 'viewer'];

/**
 * Acciones que existen de verdad en la UI de cada módulo, levantadas de una
 * auditoría de sus botones. El modal de permisos solo ofrece estas: un checkbox
 * de "aprobar" en Calendario no controla nada y solo confunde al admin.
 *
 * Los módulos ausentes son de solo lectura (ver DEFAULT_MODULE_ACTIONS).
 * Cuando un módulo gane una acción nueva, hay que agregarla aquí junto con su
 * gating; los usuarios con mapa guardado la tendrán denegada hasta que el
 * admin la marque.
 */
export const MODULE_ACTION_MATRIX: Record<string, ModuleAction[]> = {
  // approve = aprobar/rechazar solicitudes de embarque
  shipments: ['read', 'create', 'update', 'delete', 'export', 'approve'],
  // approve = confirmar la orden
  'purchase-orders': ['read', 'create', 'update', 'delete', 'export', 'approve'],
  // export = descargar PDF; approve = aceptar/rechazar; sin delete en la UI
  quotes: ['read', 'create', 'update', 'export', 'approve'],
  reports: ['read', 'create', 'update', 'delete', 'export'],
  tracking: ['read', 'create'],
  // manage_templates = crear, editar, duplicar y borrar plantillas de correo
  emails: ['read', 'create', 'manage_templates'],
  pickup: ['read', 'create'],
  warehouses: ['read', 'create', 'update'],
  warehouse: ['read', 'create', 'update'],
  docgen: ['read', 'create', 'update', 'delete'],
  dashboards: ['read', 'create', 'update', 'delete'],
  settings: ['read', 'update'],
  import_data: ['read', 'create'],
  'usage-analytics': ['read', 'export'],
  support: ['read', 'create']
};

export const DEFAULT_MODULE_ACTIONS: readonly ModuleAction[] = ['read'];

/** Acciones configurables para un módulo; solo lectura si no está en la matriz. */
export function actionsForModule(moduleId: string): readonly ModuleAction[] {
  return MODULE_ACTION_MATRIX[moduleId] ?? DEFAULT_MODULE_ACTIONS;
}

export const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Administrador',
  regular: 'Regular',
  viewer: 'Solo vista',
  superadmin: 'Super Admin Kila'
};

/** Acciones que un usuario tiene sobre cada módulo dentro de una compañía. */
export type ModulePermissions = Record<string, ModuleAction[]>;

/**
 * Permisos indexados por compañía. El rol es global, pero los permisos no: el
 * admin de la compañía A no debe poder alterar a qué accede el usuario dentro
 * de la compañía B.
 */
export type CompanyPermissions = Record<string, ModulePermissions>;

/**
 * Traduce el rol almacenado al modelo actual.
 *
 * Compatibilidad: hoy todos los usuarios tienen role 'admin' y la condición de
 * "solo vista" vive en clientType, así que clientType manda sobre el rol. Los
 * roles del modelo anterior (client_administrator, supplier, reader, …) caen a
 * 'regular'. Devuelve null si el usuario no tiene rol, que significa sin acceso.
 *
 * Esta compatibilidad se puede retirar una vez migrados todos los usuarios.
 */
export function resolveUserRole(
  role?: string | null,
  clientType?: string | null
): UserRole | null {
  if (!role) return null;
  if (role === 'superadmin') return 'superadmin';
  if (clientType === 'view') return 'viewer';
  if (role === 'admin' || role === 'regular' || role === 'viewer') return role;
  return 'regular';
}

/** clientType que corresponde a un rol, mientras el campo siga existiendo. */
export function clientTypeForRole(role: UserRole): ClientType {
  return role === 'viewer' ? 'view' : 'regular';
}

/**
 * Indica si un usuario regular puede ejecutar una acción sobre un módulo.
 *
 * Un regular sin mapa de permisos para la compañía (usuario todavía sin migrar,
 * o recién invitado) conserva acceso completo: el criterio acordado es que los
 * regulares arrancan con todo y el admin va recortando.
 */
export function regularHasAction(
  permissions: CompanyPermissions | undefined,
  companyId: string,
  moduleId: string,
  action: ModuleAction
): boolean {
  const modules = permissions?.[companyId];
  if (!modules) return true;
  return (modules[moduleId] ?? []).includes(action);
}
